import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Folder, Image, ChevronRight, Grid3X3, Eye, Heart } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getCategorizedLocalLibrary, getCategoryEpisodeImages, getAllFavorites, addEpisodeFavorite, removeEpisodeFavorite, addWorkFavorite, removeWorkFavorite, type CategoryInfo, type EpisodeInfo } from '../api/fancaps'
import AuthenticatedImage from '../components/AuthenticatedImage'

const CategorizedLibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<CategoryInfo | null>(null)
  const [selectedEpisode, setSelectedEpisode] = useState<EpisodeInfo | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const limit = 10000 // 和后端保持一致，支持电影级大量图片

  // 获取分类数据
  const {
    data: libraryData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['categorized-library'],
    queryFn: getCategorizedLocalLibrary,
    staleTime: 30 * 60 * 1000, // 30分钟缓存
  })

  // 获取特定集数的详细图片
  const {
    data: episodeImagesData,
    isLoading: isLoadingEpisode
  } = useQuery({
    queryKey: ['category-episode-images', selectedCategory?.show_name, selectedEpisode?.episode_name, currentPage],
    queryFn: () => {
      if (!selectedCategory || !selectedEpisode) return null
      return getCategoryEpisodeImages(selectedCategory.show_name, selectedEpisode.episode_name, currentPage, limit)
    },
    enabled: !!selectedCategory && !!selectedEpisode,
    staleTime: 10 * 60 * 1000,
  })

  // 获取收藏数据
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: getAllFavorites,
    staleTime: 30 * 1000,
  })

  // 处理剧集收藏
  const handleEpisodeFavorite = async (episode: EpisodeInfo, isFavorited: boolean) => {
    if (!selectedCategory) return
    
    try {
      if (isFavorited) {
        // 需要找到episode_id来删除收藏
        const episodeFavorite = favoritesData?.data?.episodes?.find(e => 
          e.title === episode.episode_name && e.work_title === selectedCategory.show_name
        )
        if (episodeFavorite) {
          await removeEpisodeFavorite(episodeFavorite.id)
        }
      } else {
        // 构造episode URL（这里简化处理，实际可能需要更准确的URL）
        const episodeUrl = `/episodes/${selectedCategory.show_name}/${episode.episode_name}`
        const previewImages = episode.images.slice(0, 4).map(img => img.small_url)
        
        await addEpisodeFavorite(
          episode.episode_name,
          selectedCategory.show_name,
          selectedCategory.show_name, // 使用show_name作为work_id，可能需要调整
          episodeUrl,
          previewImages
        )
      }
      // 刷新收藏数据
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    } catch (error) {
      console.error('收藏操作失败:', error)
    }
  }

  // 处理作品收藏
  const handleWorkFavorite = async (category: CategoryInfo, isFavorited: boolean) => {
    try {
      if (isFavorited) {
        // 需要找到work_id来删除收藏
        const workFavorite = favoritesData?.data?.works?.find(w => w.title === category.show_name)
        if (workFavorite) {
          await removeWorkFavorite(workFavorite.id)
        }
      } else {
        // 构造work URL（这里简化处理）
        const workUrl = `/categorized-library/${category.show_name}`
        await addWorkFavorite(category.show_name, 'Anime', workUrl)
      }
      // 刷新收藏数据
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    } catch (error) {
      console.error('收藏操作失败:', error)
    }
  }

  // 检查剧集是否已收藏
  const isEpisodeFavorited = (episodeName: string, workName: string): boolean => {
    return favoritesData?.data?.episodes?.some(episode => 
      episode.title === episodeName && episode.work_title === workName
    ) || false
  }

  // 检查作品是否已收藏
  const isWorkFavorited = (workName: string): boolean => {
    return favoritesData?.data?.works?.some(work => work.title === workName) || false
  }

  const handleBackClick = () => {
    if (selectedEpisode) {
      setSelectedEpisode(null)
      setCurrentPage(1)
    } else if (selectedCategory) {
      setSelectedCategory(null)
    } else {
      navigate('/')
    }
  }

  const handleCategoryClick = (category: CategoryInfo) => {
    setSelectedCategory(category)
    setSelectedEpisode(null)
    setCurrentPage(1)
  }

  const handleEpisodeClick = (episode: EpisodeInfo) => {
    setSelectedEpisode(episode)
    setCurrentPage(1)
  }

  const getImageProxyUrl = (imageId: string) => {
    return `/api/images/${imageId}.jpg`
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回首页
          </button>
          <h1 className="text-2xl font-bold text-gray-900">分类图片库</h1>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">
              加载分类图片库失败: {error instanceof Error ? error.message : '未知错误'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 标题和导航 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={handleBackClick}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            {selectedEpisode ? '返回集数列表' : selectedCategory ? '返回作品列表' : '返回首页'}
          </button>
          
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>分类图片库</span>
            {selectedCategory && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span>{selectedCategory.show_name}</span>
              </>
            )}
            {selectedEpisode && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span>{selectedEpisode.episode_name}</span>
              </>
            )}
          </div>
        </div>

        {libraryData && (
          <div className="text-sm text-gray-600">
            共 {libraryData.total_categories} 个作品，{libraryData.total_images} 张图片
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">正在加载分类图片库...</p>
          </div>
        </div>
      ) : selectedEpisode ? (
        // 显示特定集数的图片
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-medium text-blue-900 mb-2">
              {selectedCategory?.show_name} - {selectedEpisode.episode_name}
            </h2>
            <p className="text-blue-700 text-sm">
              共 {selectedEpisode.image_count} 张图片
            </p>
          </div>

          {isLoadingEpisode ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : episodeImagesData && episodeImagesData.images.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
              {episodeImagesData.images.map((image) => (
                <div key={image.id} className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all">
                  <div className="aspect-video relative overflow-hidden bg-gray-100">
                    <AuthenticatedImage
                      src={getImageProxyUrl(image.id)}
                      alt={`图片 ${image.id}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                      {image.id}
                    </div>
                    {/* 预览按钮 */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation()
                          window.open(getImageProxyUrl(image.id), '_blank')
                        }}
                        className="bg-white bg-opacity-90 text-gray-800 rounded-full p-3 hover:bg-white transition-all"
                      >
                        <Eye className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              此集数暂无图片
            </div>
          )}

          {/* 分页控制 */}
          {episodeImagesData && episodeImagesData.total > limit && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                上一页
              </button>
              <span className="px-3 py-1">
                第 {currentPage} 页，共 {Math.ceil(episodeImagesData.total / limit)} 页
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(episodeImagesData.total / limit)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      ) : selectedCategory ? (
        // 显示选中作品的集数列表
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-medium text-blue-900 mb-2">
              {selectedCategory.show_name}
            </h2>
            <p className="text-blue-700 text-sm">
              共 {selectedCategory.episode_count} 集，{selectedCategory.total_images} 张图片
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedCategory.episodes.map((episode) => {
              const isFavorited = isEpisodeFavorited(episode.episode_name, selectedCategory.show_name)
              return (
                <div
                  key={episode.episode_name}
                  className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-lg transition-all relative"
                >
                  {/* 收藏按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEpisodeFavorite(episode, isFavorited)
                    }}
                    className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                      isFavorited 
                        ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                        : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                    } shadow-sm border border-gray-200`}
                    title={isFavorited ? '取消收藏' : '收藏剧集'}
                  >
                    <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                  </button>

                  <div 
                    onClick={() => handleEpisodeClick(episode)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-3 pr-10">
                      <h3 className="font-medium text-gray-900 group-hover:text-blue-600">
                        {episode.episode_name}
                      </h3>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                    </div>
                
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span className="flex items-center">
                        <Image className="w-4 h-4 mr-1" />
                        {episode.image_count} 张图片
                      </span>
                      <span className="text-blue-600">
                        查看详情
                      </span>
                    </div>

                    {/* 预览图网格 */}
                    {episode.images.length > 0 && (
                      <div className="mt-3 grid grid-cols-4 gap-1">
                        {episode.images.slice(0, 4).map((image) => (
                          <div key={image.id} className="aspect-square bg-gray-100 rounded overflow-hidden">
                            <AuthenticatedImage
                              src={getImageProxyUrl(image.id)}
                              alt={`预览 ${image.id}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        // 显示作品列表
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h2 className="text-lg font-medium text-green-900 mb-2">
              📚 本地图片库分类浏览
            </h2>
            <p className="text-green-700 text-sm">
              根据图片ID智能分类，按作品和集数组织浏览
            </p>
          </div>

          {libraryData && libraryData.categories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {libraryData.categories.map((category) => {
                const isFavorited = isWorkFavorited(category.show_name)
                return (
                  <div
                    key={category.show_name}
                    onClick={() => handleCategoryClick(category)}
                    className="group cursor-pointer bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-lg transition-all relative"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <Folder className="w-6 h-6 text-blue-600 mr-3" />
                        <h3 className="font-medium text-gray-900 group-hover:text-blue-600">
                          {category.show_name}
                        </h3>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center justify-between">
                        <span>集数</span>
                        <span className="font-medium">{category.episode_count} 集</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>图片</span>
                        <span className="font-medium">{category.total_images} 张</span>
                      </div>
                    </div>

                    {/* 预览图网格 */}
                    <div className="mt-4 grid grid-cols-4 gap-1">
                      {category.episodes.slice(0, 4).map((episode) => (
                        episode.images.slice(0, 1).map((image) => (
                          <div key={image.id} className="aspect-square bg-gray-100 rounded overflow-hidden">
                            <AuthenticatedImage
                              src={getImageProxyUrl(image.id)}
                              alt={`预览 ${image.id}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ))
                      ))}
                    </div>

                    {/* 作品收藏按钮 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleWorkFavorite(category, isFavorited)
                      }}
                      className={`absolute bottom-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                        isFavorited 
                          ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                          : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                      } shadow-sm border border-gray-200`}
                      title={isFavorited ? '取消收藏' : '收藏作品'}
                    >
                      <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              暂无分类图片数据
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CategorizedLibraryPage